// App chrome (DevRobot UI kit): floating sidebar (collapsible to icons), frosted top bar,
// iOS-style tab bar below lg, command palette and keyboard shortcuts.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import type { UserDTO } from '@devrobot/shared';
import { ChevronsUpDown, Ellipsis, Keyboard, LogOut, Menu, PanelLeft, Search, Settings } from 'lucide-react';
import { ApiError, api } from '../../api/client';
import { LiveEventsProvider, useLiveStatus, type LiveStatus } from '../../api/events';
import { useUnreadCount } from '../../api/notifications';
import { useMe, useMeta, useOverview } from '../../api/queries';
import { useShortcuts } from '../../api/shortcuts';
import { UserProvider, useUser } from '../../api/user';
import { useDesktopPwa } from '../../kit/hooks';
import { glide } from '../../kit/motion';
import { setBadge } from '../../kit/native';
import CommandPalette, { openCommand, openKeyboardHelp } from '../CommandPalette';
import { NotificationBell } from '../notifications/NotificationBell';
import { ErrorAlert } from '../ErrorAlert';
import { RobotMark, Wordmark } from '../Logo';
import { ThemeToggle } from '../ThemeToggle';
import { BackButton } from './BackButton';
import { SidebarShortcuts } from './SidebarShortcuts';
import { DRAWER_ID, closeDrawer, watchDrawerAcrossVisibility } from './drawer';
import { ALL_NAV, NAV, isMac, navFor, type NavItem } from './nav';

function FullPage({ children }: { children: ReactNode }) {
  return (
    <main className="relative flex min-h-dvh items-center justify-center px-4 dr-safe-pt-4 dr-safe-pb-4">
      <div aria-hidden="true" className="dr-aurora pointer-events-none fixed inset-x-0 top-0 -z-10 h-[34rem]" />
      {children}
    </main>
  );
}

/** Authenticated shell: loads the session, then renders the app chrome. */
export default function AppLayout() {
  const me = useMe();
  if (me.isPending) {
    return (
      <FullPage>
        <div className="flex animate-fade flex-col items-center gap-4" role="status">
          <RobotMark className="size-14" />
          <span className="loading loading-dots loading-md text-primary" aria-label="Loading your session" />
        </div>
      </FullPage>
    );
  }
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return (
        <FullPage>
          <p role="status" className="animate-fade">
            Redirecting to sign-in… <Link className="link link-primary" to="/login">Continue</Link>
          </p>
        </FullPage>
      );
    }
    return (
      <FullPage>
        <div className="w-full max-w-lg animate-rise">
          <ErrorAlert error={me.error} title="Could not load your session" onRetry={() => void me.refetch()} />
        </div>
      </FullPage>
    );
  }
  return (
    <UserProvider user={me.data.user}>
      <LiveEventsProvider>
        <Shell />
      </LiveEventsProvider>
    </UserProvider>
  );
}

function Shell() {
  const location = useLocation();
  const drawer = useRef<HTMLInputElement>(null);
  const [collapsed, setCollapsed] = useState(() => document.documentElement.dataset.sidebar === 'collapsed');
  const current = navFor(location.pathname);

  useEffect(() => {
    document.documentElement.dataset.sidebar = collapsed ? 'collapsed' : 'expanded';
    try {
      localStorage.setItem('devrobot:sidebar', collapsed ? 'collapsed' : 'expanded');
    } catch {
      /* storage unavailable: the choice lasts for this visit */
    }
  }, [collapsed]);

  // Close the mobile drawer after navigating.
  useEffect(() => {
    closeDrawer();
  }, [location.pathname]);

  // A route change is not the only way to leave the drawer behind: a shortcut leaves for an
  // application host, which changes no route at all. Without this, returning to the app finds
  // the drawer still checked, and because every control is a <label> that toggles, the next
  // tap on Menu closes it -- leaving the overlay covering the screen.
  useEffect(watchDrawerAcrossVisibility, []);

  useEffect(() => {
    document.title = current ? `${current.label} · DevRobot` : 'DevRobot';
  }, [current]);

  useKeyboardShortcuts();
  useKeyboardOpen();
  useAppBadge();
  usePushNavigation();

  return (
    <div className="drawer relative z-[1] min-h-dvh lg:drawer-open">
      <input id={DRAWER_ID} type="checkbox" className="drawer-toggle" ref={drawer} aria-label="Toggle navigation" />
      <div className="drawer-content flex min-w-0 flex-col overflow-x-clip">
        <a href="#main" className="btn btn-sm btn-primary fixed left-3 top-3 z-[70] -translate-y-24 focus:translate-y-[var(--dr-safe-top)]">
          Skip to content
        </a>
        <Topbar title={current?.label} collapsed={collapsed} onCollapse={() => setCollapsed((c) => !c)} />
        <main
          id="main"
          tabIndex={-1}
          className="page-view mx-auto w-full max-w-[96rem] flex-1 px-4 pt-6 pb-[calc(var(--dr-dock)+var(--dr-safe-bottom)+2rem)] outline-none sm:px-6 lg:px-8 lg:pb-16"
        >
          <Outlet />
        </main>
      </div>
      <Sidebar current={current} />
      <TabBar current={current} />
      <KeyboardShortcutsDialog />
      <CommandPalette />
      <div aria-hidden="true" className="dr-aurora pointer-events-none fixed inset-x-0 top-0 -z-10 h-[34rem]" />
    </div>
  );
}

/* ── Sidebar ────────────────────────────────────────────────────────────── */
function Sidebar({ current }: { current?: NavItem }) {
  const nav = useRef<HTMLElement>(null);
  const glider = useRef<HTMLSpanElement>(null);
  const meta = useMeta();
  const shortcuts = useShortcuts().data ?? [];
  const overview = useOverview().data;
  const counts = overview?.counts;
  const attention = overview?.attention.length ?? 0;

  // Inside a route View Transition the glider jumps and the transition morphs it;
  // without View Transitions its own CSS transition does the gliding.
  useLayoutEffect(() => {
    glide(glider.current, current ? nav.current?.querySelector(`[data-nav="${current.to}"]`) : null, { instant: 'startViewTransition' in document });
    // `shortcuts.length`: the glider measures against its own nav, so the section above cannot
    // displace it -- but recomputing when that section appears guards against future layout changes.
  }, [current, shortcuts.length]);

  useEffect(() => {
    const place = () => glide(glider.current, current ? nav.current?.querySelector(`[data-nav="${current.to}"]`) : null, { instant: true });
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
    if (nav.current) observer?.observe(nav.current);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [current]);

  const extras: Record<string, ReactNode> = {
    '/': attention > 0 ? <span className="nav-label badge badge-soft badge-warning badge-sm tabular-nums lg:collapsed:hidden" title={`${attention} item${attention === 1 ? '' : 's'} need attention`}>{attention}</span> : null,
    '/jobs':
      counts && counts.runningJobs > 0 ? (
        <span className="nav-label inline-flex items-center gap-1.5 text-[11px] font-medium text-base-content/55 tabular-nums lg:collapsed:hidden" title={`${counts.runningJobs} active job${counts.runningJobs === 1 ? '' : 's'}`}>
          <LiveDot tone="info" />
          {counts.runningJobs}
        </span>
      ) : null,
  };

  return (
    <div className="drawer-side z-40 lg:collapsed:overflow-visible">
      <label htmlFor="app-drawer" aria-label="Close navigation" className="drawer-overlay" />
      <aside className="flex h-dvh dr-chrome dr-safe-pt-3 dr-safe-pb-3 dr-safe-pl-3 pr-3 lg:pr-0">
        <div className="sidebar-panel dr-surface flex h-full w-64 flex-col gap-4 overflow-y-auto p-3 lg:collapsed:w-[4.5rem] lg:collapsed:overflow-visible">
          <Link to="/" className="dr-focus flex items-center gap-2.5 rounded-field px-1 py-1" aria-label="DevRobot overview">
            <RobotMark className="size-9" />
            <span className="nav-label">
              <Wordmark />
            </span>
          </Link>

          <SidebarShortcuts items={shortcuts} />

          <nav ref={nav} className="relative" aria-label="Main">
            <span ref={glider} aria-hidden="true" className="nav-glider pointer-events-none absolute inset-x-0 top-0 z-0 rounded-field bg-primary/10 opacity-0 ring-1 ring-primary/15 ring-inset" />
            {NAV.map((group, gi) => (
              <div key={group.heading}>
                <p className={`nav-label px-3 pb-1.5 text-[11px] font-semibold tracking-[0.08em] text-base-content/45 uppercase ${gi > 0 ? 'pt-5' : ''}`}>{group.heading}</p>
                <ul className="space-y-0.5">
                  {group.items.map((item) => (
                    <SideItem key={item.to} item={item} active={current?.to === item.to} extra={extras[item.to]} />
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="mt-auto space-y-3">
            <p className="nav-label flex flex-wrap items-center gap-1.5 px-2 text-xs text-base-content/50 lg:collapsed:hidden">
              DevRobot {meta.data?.version ?? ''}
              {meta.data?.gitlab.mode === 'simulator' ? <span className="badge badge-soft badge-warning badge-xs">GitLab simulator</span> : null}
            </p>
            <UserMenu />
          </div>
        </div>
      </aside>
    </div>
  );
}

function SideItem({ item, active, extra }: { item: NavItem; active: boolean; extra?: ReactNode }) {
  const Icon = item.icon;
  return (
    <li className="w-full lg:collapsed:tooltip lg:collapsed:tooltip-right" data-tip={item.label}>
      <Link
        to={item.to}
        data-nav={item.to}
        aria-current={active ? 'page' : undefined}
        className="dr-focus group relative z-10 flex h-10 items-center gap-3 rounded-field px-3 text-sm font-medium text-base-content/70 transition-colors duration-200 hover:bg-base-content/[0.045] hover:text-base-content aria-[current=page]:text-base-content aria-[current=page]:hover:bg-transparent"
      >
        <Icon className="size-[1.125rem] shrink-0 transition-colors duration-300 group-aria-[current=page]:text-primary" aria-hidden="true" />
        <span className="nav-label min-w-0 flex-1 truncate">{item.label}</span>
        {extra}
      </Link>
    </li>
  );
}

function LiveDot({ tone }: { tone: 'info' | 'success' | 'warning' | 'error' }) {
  return (
    <span className="inline-grid *:[grid-area:1/1]" aria-hidden="true">
      <span className={`status status-${tone} status-sm animate-ping`} />
      <span className={`status status-${tone} status-sm`} />
    </span>
  );
}

function UserMenu() {
  const user = useUser();
  const pop = useRef<HTMLDivElement>(null);
  const [signingOut, setSigningOut] = useState(false);
  const close = () => pop.current?.hidePopover?.();

  const signOut = async () => {
    setSigningOut(true);
    try {
      await api.post('/api/v1/auth/logout', undefined, { allowUnauthenticated: true });
    } catch {
      /* the session may already be gone; sign-in page either way */
    }
    window.location.assign('/login');
  };

  return (
    <>
      <button
        type="button"
        popoverTarget="user-pop"
        style={{ anchorName: '--user' } as React.CSSProperties}
        className="dr-focus flex w-full items-center gap-3 rounded-field p-1.5 text-left transition-colors duration-200 hover:bg-base-200"
        aria-label={`Account menu for ${user.name}`}
      >
        <Avatar user={user} size="size-9" />
        <span className="nav-label min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{user.name}</span>
          <span className="block truncate text-xs text-base-content/55">@{user.username}</span>
        </span>
        <ChevronsUpDown className="nav-label size-4 text-base-content/45" aria-hidden="true" />
      </button>
      <div
        popover="auto"
        id="user-pop"
        ref={pop}
        style={{ positionAnchor: '--user' } as React.CSSProperties}
        className="dropdown dropdown-top mb-1 w-72 rounded-box border border-hairline bg-base-100 p-1.5 dr-shadow-lg"
      >
        <div className="flex items-center gap-3 p-2.5">
          <Avatar user={user} size="size-10" />
          <div className="min-w-0">
            <div className="truncate font-semibold">{user.name}</div>
            <div className="truncate text-sm text-base-content/60">@{user.username}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 px-2.5 pb-2">
          <span className="badge badge-ghost badge-sm" title="Your role in the DevRobot GitLab group">
            GitLab {user.groupRole}
          </span>
          {user.platformAdmin ? <span className="badge badge-soft badge-primary badge-sm">platform admin</span> : null}
          {user.isInstanceAdmin ? <span className="badge badge-soft badge-secondary badge-sm">instance admin</span> : null}
        </div>
        <p className="px-2.5 pb-2 text-xs text-base-content/55">Project access follows your GitLab membership.</p>
        <ul className="menu w-full border-t border-hairline p-0 pt-1.5">
          <li>
            <Link to="/settings" onClick={close}>
              <Settings className="size-4" aria-hidden="true" /> Settings and sessions
            </Link>
          </li>
          <li className="max-sm:hidden">
            <button
              type="button"
              onClick={() => {
                close();
                openKeyboardHelp();
              }}
            >
              <Keyboard className="size-4" aria-hidden="true" /> Keyboard shortcuts <kbd className="kbd kbd-xs ml-auto">?</kbd>
            </button>
          </li>
          <li>
            <button type="button" onClick={signOut} disabled={signingOut}>
              {signingOut ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : <LogOut className="size-4" aria-hidden="true" />}
              Sign out
            </button>
          </li>
        </ul>
      </div>
    </>
  );
}

export function Avatar({ user, size = 'size-8' }: { user: Pick<UserDTO, 'name' | 'username' | 'avatarUrl'>; size?: string }) {
  const initials = (user.name || user.username)
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  if (user.avatarUrl) {
    return (
      <div className="avatar shrink-0">
        <div className={`${size} rounded-full ring-1 ring-hairline`}>
          <img src={user.avatarUrl} alt="" />
        </div>
      </div>
    );
  }
  return (
    <div className="avatar avatar-placeholder shrink-0">
      <div className={`dr-tint-secondary ${size} rounded-full`}>
        <span className="text-xs font-semibold">{initials}</span>
      </div>
    </div>
  );
}

/* ── Tab bar (below lg) ─────────────────────────────────────────────────── */
// iOS-style dock for the main sections; the rest of the navigation and the account menu stay in the drawer.
function TabBar({ current }: { current?: NavItem }) {
  const list = useRef<HTMLUListElement>(null);
  const glider = useRef<HTMLSpanElement>(null);
  const tabs = ALL_NAV.filter((n) => n.tab);
  const inTabs = current && tabs.includes(current);
  const activeKey = inTabs ? current.to : 'more';

  useLayoutEffect(() => {
    glide(glider.current, list.current?.querySelector(`[data-tab="${activeKey}"]`), { axis: 'x', instant: 'startViewTransition' in document });
  }, [activeKey]);
  useEffect(() => {
    const onResize = () => glide(glider.current, list.current?.querySelector(`[data-tab="${activeKey}"]`), { axis: 'x', instant: true });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeKey]);

  const cls =
    'dr-focus relative z-10 flex h-14 flex-col items-center justify-center gap-0.5 rounded-full px-1 text-[0.6875rem] font-semibold text-base-content/60 transition-colors duration-200 aria-[current=page]:text-primary';

  return (
    <nav aria-label="Sections" className="tab-dock fixed inset-x-0 bottom-0 z-30 flex justify-center dr-chrome dr-safe-px-3 dr-safe-pb-2 lg:hidden">
      {/* Frosts whatever scrolls behind and below the bar, down through the home indicator, fading out above it. */}
      <div aria-hidden="true" className="tab-dock-blur pointer-events-none absolute inset-x-0 -top-6 bottom-0 backdrop-blur-md" />
      <ul ref={list} className="tab-pill relative flex w-full max-w-md rounded-full border border-hairline p-1 dr-shadow-lg">
        <span ref={glider} aria-hidden="true" className="tab-glider pointer-events-none absolute top-1 left-0 z-0 rounded-full bg-primary/10 opacity-0 ring-1 ring-primary/15 ring-inset" />
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.to} className="min-w-0 flex-1">
              <Link to={item.to} data-tab={item.to} aria-current={activeKey === item.to ? 'page' : undefined} className={cls}>
                <Icon className="size-[1.375rem] shrink-0" aria-hidden="true" />
                <span className="max-w-full truncate">{item.short ?? item.label}</span>
              </Link>
            </li>
          );
        })}
        <li className="min-w-0 flex-1">
          <label htmlFor="app-drawer" data-tab="more" aria-current={activeKey === 'more' ? 'page' : undefined} className={`${cls} cursor-pointer`} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), e.currentTarget.click())}>
            <Ellipsis className="size-[1.375rem] shrink-0" aria-hidden="true" />
            <span className="max-w-full truncate">More</span>
          </label>
        </li>
      </ul>
    </nav>
  );
}

// Flag <html data-keyboard> while the on-screen keyboard is up, so the dock steps aside.
function useKeyboardOpen() {
  useEffect(() => {
    const root = document.documentElement;
    const coarse = matchMedia('(pointer: coarse)');
    const typing = (el: Element | null) =>
      el instanceof HTMLElement &&
      (el.isContentEditable || el.matches('textarea, select, input:not([type=checkbox], [type=radio], [type=range], [type=button], [type=submit], [type=reset], [type=file], [type=color])'));
    const sync = () => {
      if (coarse.matches && typing(document.activeElement)) root.dataset.keyboard = '';
      else delete root.dataset.keyboard;
    };
    // focusout fires before focus lands elsewhere: wait a frame so hopping between fields doesn't flicker.
    const onFocusOut = () => requestAnimationFrame(sync);
    window.addEventListener('focusin', sync);
    window.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('focusin', sync);
      window.removeEventListener('focusout', onFocusOut);
      delete root.dataset.keyboard;
    };
  }, []);
}

/**
 * Tapping a push notification focuses an already-open window rather than opening another, and
 * the service worker asks it to navigate. Without this the app would sit on whatever page it
 * was showing and the tap would appear to do nothing.
 */
function usePushNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; href?: string } | undefined;
      if (data?.type === 'devrobot:navigate' && typeof data.href === 'string' && data.href.startsWith('/')) navigate(data.href);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
}

// Installed app: unread notifications on the Home Screen icon (when turned on in Settings).
// Deliberately not the overview's "needs attention" count: that is a list of live conditions
// which cannot reach zero while a VPS is down, and a badge nobody can clear is a badge people
// switch off -- taking notification permission with it on iOS.
function useAppBadge() {
  const unread = useUnreadCount().data?.unread;
  useEffect(() => {
    if (unread !== undefined) setBadge(unread);
  }, [unread]);
}

/* ── Top bar ────────────────────────────────────────────────────────────── */
function Topbar({ title, collapsed, onCollapse }: { title?: string; collapsed: boolean; onCollapse: () => void }) {
  const appWindow = useDesktopPwa();
  return (
    <div className="sticky top-0 z-30 dr-safe-pt-3 dr-safe-px-3 sm:dr-safe-px-4 lg:pl-6 lg:dr-safe-pr-6">
      {/* Softens content scrolling behind the bar without painting a colour, so the page glow stays seamless. */}
      <div aria-hidden="true" className="topbar-blur pointer-events-none absolute inset-0 backdrop-blur-md" />
      <header className="navbar relative min-h-14 gap-2 rounded-box border border-hairline px-2 dr-glass dr-shadow-sm dr-chrome">
        <div className="navbar-start min-w-0 gap-1">
          {appWindow ? <BackButton /> : null}
          <label htmlFor="app-drawer" className="btn btn-square btn-ghost btn-sm lg:hidden" aria-label="Open navigation">
            <Menu className="size-[1.125rem]" aria-hidden="true" />
          </label>
          <button type="button" className="btn btn-square btn-ghost btn-sm max-lg:hidden" onClick={onCollapse} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-pressed={collapsed}>
            <PanelLeft className="size-[1.125rem]" aria-hidden="true" />
          </button>
          <Link to="/" className="lg:hidden" aria-label="DevRobot overview">
            <RobotMark className="size-8" />
          </Link>
          <div className="breadcrumbs min-w-0 py-0 text-sm max-sm:hidden">
            <ul>
              <li>
                <Link to="/" className="text-base-content/55">
                  DevRobot
                </Link>
              </li>
              {title ? <li className="font-medium">{title}</li> : null}
            </ul>
          </div>
        </div>
        <div className="navbar-center max-md:hidden">
          <button type="button" onClick={openCommand} className="btn h-9 w-72 justify-start gap-2 border-hairline bg-base-100/70 font-normal text-base-content/55 shadow-none btn-sm lg:w-80">
            <Search className="size-4" aria-hidden="true" />
            <span className="flex-1 text-left">Search or jump to…</span>
            <kbd className="kbd kbd-xs">{isMac ? '⌘' : 'Ctrl'}</kbd>
            <kbd className="kbd kbd-xs">K</kbd>
          </button>
        </div>
        <div className="navbar-end gap-0.5">
          <button type="button" className="btn btn-square btn-ghost btn-sm md:hidden" onClick={openCommand} aria-label="Search">
            <Search className="size-[1.125rem]" aria-hidden="true" />
          </button>
          <NotificationBell />
          <LiveIndicator />
          <ThemeToggle />
          <button type="button" className="btn btn-square btn-ghost btn-sm max-sm:hidden" onClick={openKeyboardHelp} aria-label="Keyboard shortcuts">
            <Keyboard className="size-[1.125rem]" aria-hidden="true" />
          </button>
        </div>
      </header>
    </div>
  );
}

const LIVE: Record<LiveStatus, { tone: 'success' | 'info' | 'warning' | 'error'; label: string; tip: string }> = {
  live: { tone: 'success', label: 'Live', tip: 'Views update automatically as jobs and targets change.' },
  connecting: { tone: 'info', label: 'Connecting', tip: 'Connecting to the live change feed…' },
  reconnecting: { tone: 'warning', label: 'Reconnecting', tip: 'The live change feed dropped; data may be out of date until it reconnects.' },
  offline: { tone: 'error', label: 'Offline', tip: 'Live updates are unavailable. Data refreshes when you reload or refocus the page.' },
};

function LiveIndicator() {
  const status = useLiveStatus();
  const s = LIVE[status];
  return (
    <div className="tooltip tooltip-left lg:tooltip-bottom" data-tip={s.tip}>
      <span
        className="flex h-8 items-center gap-2 rounded-field px-2 text-xs font-medium text-base-content/65"
        role="status"
        aria-live="polite"
        tabIndex={0}
        aria-label={`Live updates: ${s.label}. ${s.tip}`}
      >
        {status === 'live' ? <LiveDot tone="success" /> : <span aria-hidden="true" className={`status status-${s.tone} status-sm animate-pulse`} />}
        <span className="max-sm:hidden">{s.label}</span>
      </span>
    </div>
  );
}

/* ── Keyboard shortcuts ─────────────────────────────────────────────────── */
function useKeyboardShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    let lastG = 0;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (t instanceof Element && t.closest('input, textarea, select, [contenteditable], dialog[open], .xterm')) return;
      if (e.key === '?') {
        e.preventDefault();
        openKeyboardHelp();
      } else if (e.key === '/') {
        e.preventDefault();
        const search = document.querySelector<HTMLElement>('[data-page-search]');
        if (search) search.focus();
        else openCommand();
      } else if (e.key === 'g') {
        lastG = Date.now();
      } else if (Date.now() - lastG < 900) {
        const hit = ALL_NAV.find((n) => n.key === e.key);
        lastG = 0;
        if (hit) {
          e.preventDefault();
          void navigate(hit.to);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
}

function KeyboardShortcutsDialog() {
  const rows: [string, string[]][] = [
    ['Open the command palette', [isMac ? '⌘' : 'Ctrl', 'K']],
    ['Search this page', ['/']],
    ...ALL_NAV.map((n): [string, string[]] => [`Go to ${n.label}`, ['g', n.key]]),
    ['Show this list', ['?']],
  ];
  return (
    <dialog id="keyboard-shortcuts" className="modal modal-bottom sm:modal-middle" aria-labelledby="keyboard-shortcuts-title">
      <div className="modal-box max-w-md">
        <h3 id="keyboard-shortcuts-title" className="text-lg font-semibold">
          Keyboard shortcuts
        </h3>
        <p className="mt-1 text-sm text-base-content/60">Move around DevRobot without leaving the keyboard.</p>
        <ul className="mt-4 divide-y divide-hairline">
          {rows.map(([label, keys]) => (
            <li key={label} className="flex items-center justify-between py-2.5 text-sm">
              <span>{label}</span>
              <span className="flex items-center gap-1">
                {keys.map((k, i) => (
                  <kbd key={i} className="kbd kbd-sm">
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <div className="modal-action">
          <form method="dialog">
            <button type="submit" className="btn">
              Close
            </button>
          </form>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="submit" tabIndex={-1}>
          close
        </button>
      </form>
    </dialog>
  );
}
